import {
  CloseCircleOutlined,
  DeleteOutlined,
  DownloadOutlined,
  FileTextOutlined,
  ForwardOutlined,
  InfoCircleOutlined,
  LoadingOutlined,
  PictureOutlined,
  PlayCircleFilled,
  RedoOutlined,
  ReloadOutlined,
  ThunderboltOutlined,
  VideoCameraAddOutlined,
  VideoCameraOutlined,
} from '@ant-design/icons'
import { Button, Drawer, Input, InputNumber, Modal, Radio, Select, Switch, Table, Tabs, Tooltip, message } from 'antd'
import { VistoeraActionConfirm } from '../ui/action'
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table'
import type { TFunction } from 'i18next'
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { formatDateTime as formatDateTimeI18n } from '../../i18n/dateFormat'
import { useUiLocale } from '../../i18n/LocaleProvider'
import type { UiLocale } from '../../i18n/types'
import { putFileToPresignedUrl, triggerBrowserDownload } from '../../api/client'
import {
  batchDeleteVideoGenTasks,
  createVideoGenTask,
  deleteVideoGenComposite,
  deleteVideoGenTask,
  getVideoGenConfig,
  listVideoGenTasks,
  videoGenAssetContentUrl,
  videoGenAssetPosterUrl,
  videoGenCompleteAssetUpload,
  videoGenComposedOutputUrl,
  videoGenCompositePosterUrl,
  videoGenInitiateAssetUpload,
  videoGenTaskOutputUrl,
  videoGenTaskPosterUrl,
  videoGenUploadAssetDirect,
  resolveImagePickerSelection,
  type VideoGenConfig,
  type VideoGenTask,
} from '../api'
import { ImageAssetPicker } from '../components/ImageAssetPicker'
import { resolveTenantVideoPlayerModalWidth, TenantVideoPlayerModal } from '../components/TenantVideoPlayerModal'
import { useWorkspaceState } from './WorkspaceContext'
import '../ui/tenant-items.css'
import './video-generation.css'

const TASK_POLL_MS = 5000
const PROGRESS_TICK_MS = 1000
const PAGE_SIZE = 10
/** UI Estimated Progress only (Batch V1 §5–§14) — not Worker-reported.
 * Baseline: 10s video ≈ 15 minutes wall time. */
const ESTIMATE_BASELINE_VIDEO_SEC = 10
const ESTIMATE_BASELINE_WALL_SEC = 15 * 60
export const FALLBACK_ASPECT_RATIO_CHOICES = ['9:16', '16:9', '4:3', '3:4', '1:1']
/** Soft UX hint when metadata is slow but bytes are still arriving. */
const PLAYER_SLOW_HINT_MS = 8000
/** Hard error only when there is no metadata AND no progress for this long.
 * Never treat "still downloading via Range" as an instant network failure. */
const PLAYER_STALL_MS = 45000

// Exported (VISTOERA_VIDEO_CONTINUATION_V1_P0 spec §5/§20 — "直接复用当前
// 视频生成页面已有组件") so `VideoContinuationPage.tsx` reuses the exact
// same reference-asset upload state/shape/UI instead of a parallel copy.
export type RefKind = 'reference_image' | 'reference_video'

export interface RefAssetState {
  file?: File
  filename: string
  previewUrl?: string
  status: 'uploading' | 'ready' | 'failed'
  assetId?: string
  error?: string
}

export const STATUS_BADGE_CLASS: Record<string, string> = {
  queued: 'tc-badge-warning',
  running: 'tc-badge-info',
  succeeded: 'tc-badge-success',
  failed: 'tc-badge-error',
  waiting_for_review: 'tc-badge-neutral',
}

// History UI V2 §25-§30 — real WxH ratio for each choice, so the CSS box
// this draws is an accurate miniature (never a generic placeholder icon)
// and the currently-selected value shows the same box, not just the
// dropdown list.
const ASPECT_RATIO_DIMENSIONS: Record<string, [number, number]> = {
  '16:9': [16, 9],
  '9:16': [9, 16],
  '4:3': [4, 3],
  '3:4': [3, 4],
  '1:1': [1, 1],
}

export function AspectRatioIcon({ ratio, size = 18 }: { ratio: string; size?: number }) {
  const [w, h] = ASPECT_RATIO_DIMENSIONS[ratio] ?? [16, 9]
  const scale = size / Math.max(w, h)
  const boxW = Math.max(4, Math.round(w * scale))
  const boxH = Math.max(4, Math.round(h * scale))
  return (
    <span className="tc-video-gen-ratio-icon" style={{ width: size, height: size }} aria-hidden="true">
      <span className="tc-video-gen-ratio-icon-box" style={{ width: boxW, height: boxH }} />
    </span>
  )
}

export function formatDateTime(iso: string | null, locale: UiLocale): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return formatDateTimeI18n(iso, locale)
}

/** Pure UI estimate — never written back to the Job / Worker contract. */
function estimateRunningProgressPct(task: VideoGenTask, nowMs: number): number {
  if (!task.started_at) return 3
  const started = new Date(task.started_at).getTime()
  if (Number.isNaN(started)) return 3
  const durationSec = Math.max(1, Number(task.generation?.duration_seconds) || ESTIMATE_BASELINE_VIDEO_SEC)
  const estimatedDuration =
    (durationSec / ESTIMATE_BASELINE_VIDEO_SEC) * ESTIMATE_BASELINE_WALL_SEC
  const ratio = (nowMs - started) / 1000 / estimatedDuration
  let progress: number
  if (ratio <= 0) progress = 3
  else if (ratio < 0.8) progress = 3 + (ratio / 0.8) * 77
  else if (ratio < 1.0) progress = 80 + ((ratio - 0.8) / 0.2) * 14
  else progress = 95
  return Math.min(95, Math.max(3, progress))
}

function canDeleteTask(task: VideoGenTask): boolean {
  return task.status !== 'running'
}

/** Video Continuation V1 (spec §9/§10) — `/tasks` already returns each
 * continuation root with its segments nested in `continuation_children`
 * (`segment_index` ASC, never a second top-level row for a segment). The
 * table itself stays a single flat AntD `<Table>` (spec §10: "不要做复杂
 * tree control，保持当前表格") — this just flattens root+children into one
 * array, in display order, so a lightweight `rowClassName`/indent marker
 * can distinguish a segment row without an actual nested tree control. */
function flattenGroupedTasks(items: VideoGenTask[]): VideoGenTask[] {
  const flat: VideoGenTask[] = []
  for (const item of items) {
    const composite = item.continuation_composite
    const children = item.continuation_children ?? []
    if (composite) {
      flat.push({ ...composite, is_master_result: true })
      flat.push({
        ...item,
        is_lineage_row: true,
        lineage_role: 'source',
        is_continuation_root: false,
      })
      flat.push(
        ...children.map((seg) => ({
          ...seg,
          is_lineage_row: true,
          lineage_role: 'segment' as const,
        })),
      )
    } else {
      flat.push(item)
      if (children.length) flat.push(...children)
    }
  }
  return flat
}

function groupSortTimestamp(item: VideoGenTask): number {
  const composite = item.continuation_composite
  const raw =
    composite?.finished_at ||
    composite?.queued_at ||
    item.finished_at ||
    item.queued_at
  return raw ? new Date(raw).getTime() : 0
}

function isMasterComposite(task: VideoGenTask): boolean {
  return isContinuousComposite(task) && !!task.is_master_result
}

function isLineageRow(task: VideoGenTask): boolean {
  return !!task.is_lineage_row
}

function taskPromptLabel(task: VideoGenTask, t: TFunction): string {
  if (task.lineage_role === 'source') return t('generation.lineage.originalVideo')
  return task.prompt || ''
}

function canPlayTask(task: VideoGenTask): boolean {
  if (task.status !== 'succeeded') return false
  if (isContinuousComposite(task)) return task.result_asset_ready || !!task.output_asset_id
  return !!task.output_asset_id
}

function isContinuousComposite(task: VideoGenTask): boolean {
  return task.result_kind === 'continuous_composite'
}

function taskPosterUrl(workspaceId: string, task: VideoGenTask): string {
  if (isContinuousComposite(task)) {
    return videoGenCompositePosterUrl(workspaceId, task.composite_id || task.id)
  }
  return videoGenTaskPosterUrl(workspaceId, task.id)
}

function taskPlaybackUrl(workspaceId: string, task: VideoGenTask): string {
  if (isContinuousComposite(task)) {
    return videoGenComposedOutputUrl(workspaceId, task.root_video_id || task.source_video_id || task.id)
  }
  return videoGenTaskOutputUrl(workspaceId, task.id)
}

export async function uploadRefAsset(
  workspaceId: string,
  file: File,
  directToR2: boolean,
  onProgress?: (pct: number) => void,
): Promise<string> {
  // P0 fix — `direct_to_r2=false` (the default, real browsers cannot PUT
  // cross-origin straight to R2 without bucket CORS this repo's R2 token
  // cannot configure) means bytes must be proxied through this backend
  // instead of PUT-ing to a presigned URL, or this call fails client-side
  // with a bare "Network Error" every time (see `VideoGenConfig.direct_to_r2`
  // docstring).
  if (!directToR2) {
    const { data } = await videoGenUploadAssetDirect(workspaceId, file)
    return data.id
  }
  const { data: initiated } = await videoGenInitiateAssetUpload(workspaceId, {
    filename: file.name,
    content_type: file.type || 'application/octet-stream',
    size: file.size,
  })
  await putFileToPresignedUrl(initiated.upload_url, file, onProgress)
  await videoGenCompleteAssetUpload(workspaceId, initiated.asset_id)
  return initiated.asset_id
}

type RefPreviewState = { kind: 'image' | 'video'; url: string; title: string } | null

export default function VideoGenerationPage() {
  const { t } = useTranslation(['video', 'actions', 'status'])
  const { locale } = useUiLocale()
  const { workspaceId = '' } = useParams()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { primarySite } = useWorkspaceState()
  const siteId = primarySite?.id ?? ''
  const [cfg, setCfg] = useState<VideoGenConfig | null>(null)
  const [cfgError, setCfgError] = useState<string | null>(null)

  const [prompt, setPrompt] = useState('')
  // History UI V2 §24 — 9:16 is the product default everywhere (frontend
  // initial state here, form initial value once `cfg` loads below, backend
  // default in `video_generation_params.DEFAULT_ASPECT_RATIO`, and the
  // redo-populate fallback) — never a second hardcoded "16:9".
  const [aspectRatio, setAspectRatio] = useState('9:16')
  const [qualityTier, setQualityTier] = useState('1080P')
  const [duration, setDuration] = useState(10)
  const [seedMode, setSeedMode] = useState<'random' | 'fixed'>('random')
  const [seed, setSeed] = useState<number | null>(null)
  const [enhancePrompt, setEnhancePrompt] = useState(false)
  const [advancedOpen, setAdvancedOpen] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const [refImage, setRefImage] = useState<RefAssetState | null>(null)
  const [refVideo, setRefVideo] = useState<RefAssetState | null>(null)
  const [imagePickerOpen, setImagePickerOpen] = useState(false)
  const videoInputRef = useRef<HTMLInputElement>(null)

  const [tab, setTab] = useState('all')
  const [tasks, setTasks] = useState<VideoGenTask[]>([])
  const [tasksLoading, setTasksLoading] = useState(false)
  const [tablePage, setTablePage] = useState(1)
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [nowMs, setNowMs] = useState(() => Date.now())
  const progressHighWaterRef = useRef<Map<string, number>>(new Map())

  const [playerUrl, setPlayerUrl] = useState<string | null>(null)
  const [playerTaskId, setPlayerTaskId] = useState<string | null>(null)
  const [playerTask, setPlayerTask] = useState<VideoGenTask | null>(null)
  // Player state machine (Delivery Reliability P0): IDLE→LOADING→READY|ERROR.
  // Soft "slow" hint ≠ hard timeout. Progress/`loadedmetadata` reset timers.
  const [playerPhase, setPlayerPhase] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle')
  const [playerSlowHint, setPlayerSlowHint] = useState(false)
  const [playerErrorKind, setPlayerErrorKind] = useState<'timeout' | 'media' | null>(null)
  const [playerReloadNonce, setPlayerReloadNonce] = useState(0)
  const playerVideoRef = useRef<HTMLVideoElement | null>(null)
  const playerSessionRef = useRef(0)
  const playerStallTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const playerSlowTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const playerHadProgressRef = useRef(false)
  const playerAutoRetryUsedRef = useRef(false)
  const playerBlobRef = useRef<string | null>(null)

  const revokePlayerBlob = () => {
    if (playerBlobRef.current) {
      URL.revokeObjectURL(playerBlobRef.current)
      playerBlobRef.current = null
    }
  }

  useEffect(() => {
    return () => {
      if (playerStallTimer.current) clearTimeout(playerStallTimer.current)
      if (playerSlowTimer.current) clearTimeout(playerSlowTimer.current)
    }
  }, [])

  const [refPreview, setRefPreview] = useState<RefPreviewState>(null)
  const [paramsTask, setParamsTask] = useState<VideoGenTask | null>(null)

  useEffect(() => {
    if (!workspaceId) return
    getVideoGenConfig(workspaceId)
      .then((r) => {
        setCfg(r.data)
        setAspectRatio(r.data.default_aspect_ratio)
        setQualityTier(r.data.default_quality_tier)
        setDuration(r.data.default_duration_seconds)
      })
      .catch((e) => setCfgError(e.message || t('generation.toast.configLoadFailed')))
  }, [workspaceId, t])

  // AI Chat → Video Generation handoff: `?reference_image_picker_item_id=sa:…`
  // materializes the canonical site asset into a ready video_gen reference image.
  useEffect(() => {
    const pickerItemId = searchParams.get('reference_image_picker_item_id')?.trim()
    if (!pickerItemId || !workspaceId) return
    let cancelled = false
    ;(async () => {
      try {
        const { data } = await resolveImagePickerSelection(workspaceId, {
          picker_item_id: pickerItemId,
          target: 'video_gen_asset',
        })
        if (cancelled) return
        setRefImage({
          filename: t('generation.refPreview.aiGeneratedImage'),
          previewUrl: videoGenAssetPosterUrl(workspaceId, data.asset_id),
          status: 'ready',
          assetId: data.asset_id,
        })
        setSearchParams({}, { replace: true })
      } catch (e) {
        if (!cancelled) message.error((e as Error).message || t('generation.toast.referenceImageLoadFailed'))
      }
    })()
    return () => {
      cancelled = true
    }
  }, [workspaceId, searchParams, setSearchParams, t])

  const loadTasks = useCallback(async () => {
    if (!workspaceId) return
    try {
      const { data } = await listVideoGenTasks(workspaceId, tab)
      setTasks(data.items)
    } catch {
      // Transient poll failures are silent — the table simply keeps its
      // last-known state until the next successful poll (spec: never a
      // disruptive error toast for a background refresh).
    }
  }, [workspaceId, tab])

  useEffect(() => {
    setTasksLoading(true)
    loadTasks().finally(() => setTasksLoading(false))
    const t = window.setInterval(loadTasks, TASK_POLL_MS)
    return () => window.clearInterval(t)
  }, [loadTasks])

  useEffect(() => {
    setTablePage(1)
    setSelectedIds([])
  }, [tab])

  const hasRunning = tasks.some((t) => t.status === 'running' && t.started_at)
  useEffect(() => {
    if (!hasRunning) return
    const t = window.setInterval(() => setNowMs(Date.now()), PROGRESS_TICK_MS)
    return () => window.clearInterval(t)
  }, [hasRunning])

  const displayProgressPct = useCallback(
    (task: VideoGenTask): number | null => {
      if (task.status === 'succeeded') {
        progressHighWaterRef.current.set(task.id, 100)
        return 100
      }
      if (task.status === 'queued') return null
      if (task.status === 'failed') {
        // Vistoera Video Queue / Premature Failure P0 (spec item 八) — a
        // failed task must never keep showing whatever time-based estimate
        // it happened to reach in the seconds before it failed (the exact
        // "进度停在约 4%" symptom real task 6555cd89 showed, from a Job that
        // failed 5s after being claimed — nowhere near real generation
        // progress). "—" is honest; a frozen fake percentage is not.
        return null
      }
      if (task.status === 'waiting_for_review') {
        return progressHighWaterRef.current.get(task.id) ?? null
      }
      if (task.status === 'running') {
        const calc = estimateRunningProgressPct(task, nowMs)
        const prev = progressHighWaterRef.current.get(task.id) ?? 0
        const next = Math.max(prev, calc)
        progressHighWaterRef.current.set(task.id, next)
        return next
      }
      return null
    },
    [nowMs],
  )

  const maxDurationForQuality = useMemo(() => {
    if (!cfg) return 15
    return cfg.max_duration_seconds[qualityTier] ?? cfg.max_duration_seconds.default ?? 15
  }, [cfg, qualityTier])

  useEffect(() => {
    if (duration > maxDurationForQuality) setDuration(maxDurationForQuality)
  }, [maxDurationForQuality, duration])

  const promptMax = cfg?.prompt_max_length ?? 2000

  const pickFile = (kind: RefKind) => {
    if (kind === 'reference_image') {
      if (!siteId) {
        message.error(t('generation.toast.noSiteForPicker'))
        return
      }
      setImagePickerOpen(true)
      return
    }
    videoInputRef.current?.click()
  }

  const onFileSelected = async (kind: RefKind, file: File | undefined) => {
    if (!file || !workspaceId) return
    const setState = kind === 'reference_image' ? setRefImage : setRefVideo
    const previewUrl = kind === 'reference_image' ? URL.createObjectURL(file) : undefined
    setState({ file, filename: file.name, previewUrl, status: 'uploading' })
    try {
      const assetId = await uploadRefAsset(workspaceId, file, cfg?.direct_to_r2 ?? false)
      // A reference video gets its real first-frame poster (lazily
      // generated server-side) as its card preview instead of the
      // generic icon, once upload is confirmed (History UI V2 §21).
      const readyPreviewUrl = kind === 'reference_video' ? videoGenAssetPosterUrl(workspaceId, assetId) : previewUrl
      setState({ file, filename: file.name, previewUrl: readyPreviewUrl, status: 'ready', assetId })
    } catch (e) {
      setState({ file, filename: file.name, previewUrl, status: 'failed', error: (e as Error).message || t('generation.toast.uploadFailed') })
    }
  }

  const clearRef = (kind: RefKind) => {
    if (kind === 'reference_image') {
      if (refImage?.previewUrl?.startsWith('blob:')) URL.revokeObjectURL(refImage.previewUrl)
      setRefImage(null)
    } else {
      setRefVideo(null)
      if (videoInputRef.current) videoInputRef.current.value = ''
    }
  }

  const clearPlayerTimers = () => {
    if (playerStallTimer.current) {
      clearTimeout(playerStallTimer.current)
      playerStallTimer.current = null
    }
    if (playerSlowTimer.current) {
      clearTimeout(playerSlowTimer.current)
      playerSlowTimer.current = null
    }
  }

  const failPlayer = (session: number, kind: 'timeout' | 'media') => {
    if (playerSessionRef.current !== session) return
    clearPlayerTimers()
    setPlayerPhase('error')
    setPlayerErrorKind(kind)
  }

  const scheduleStallWatchdog = (session: number) => {
    if (playerStallTimer.current) clearTimeout(playerStallTimer.current)
    playerStallTimer.current = setTimeout(() => {
      if (playerSessionRef.current !== session) return
      if (!playerAutoRetryUsedRef.current) {
        playerAutoRetryUsedRef.current = true
        setPlayerReloadNonce((n) => n + 1)
        armPlayerWatchdogs(session)
        return
      }
      failPlayer(session, 'timeout')
    }, PLAYER_STALL_MS)
  }

  const armPlayerWatchdogs = (session: number) => {
    clearPlayerTimers()
    playerHadProgressRef.current = false
    playerSlowTimer.current = setTimeout(() => {
      if (playerSessionRef.current !== session) return
      setPlayerSlowHint(true)
    }, PLAYER_SLOW_HINT_MS)
    scheduleStallWatchdog(session)
  }

  /** Video Delivery Playback — native `<video src={stable output URL}>` with
   * cookie auth + Range (see api.ts `videoGenTaskOutputUrl` doc). */
  const openPlayer = (task: VideoGenTask) => {
    if (!workspaceId) return
    const session = playerSessionRef.current + 1
    playerSessionRef.current = session
    clearPlayerTimers()
    playerAutoRetryUsedRef.current = false
    revokePlayerBlob()
    setPlayerTask(task)
    setPlayerTaskId(task.id)
    setPlayerPhase('loading')
    setPlayerSlowHint(false)
    setPlayerErrorKind(null)
    setPlayerUrl(taskPlaybackUrl(workspaceId, task))
    armPlayerWatchdogs(session)
  }

  const retryPlayer = (opts?: { preserveAutoRetryBudget?: boolean }) => {
    if (!playerTaskId || !workspaceId || !playerTask) return
    const session = playerSessionRef.current + 1
    playerSessionRef.current = session
    clearPlayerTimers()
    if (!opts?.preserveAutoRetryBudget) playerAutoRetryUsedRef.current = false
    setPlayerPhase('loading')
    setPlayerSlowHint(false)
    setPlayerErrorKind(null)
    revokePlayerBlob()
    setPlayerUrl(taskPlaybackUrl(workspaceId, playerTask))
    setPlayerReloadNonce((n) => n + 1)
    armPlayerWatchdogs(session)
  }

  const handlePlayerReady = () => {
    clearPlayerTimers()
    setPlayerSlowHint(false)
    setPlayerPhase('ready')
  }

  const handlePlayerProgress = () => {
    // Bytes still arriving (incl. moov-at-end Range seeks) — extend the stall
    // window instead of declaring "网络超时" while the download is healthy.
    playerHadProgressRef.current = true
    scheduleStallWatchdog(playerSessionRef.current)
  }

  const handlePlayerError = () => {
    const session = playerSessionRef.current
    if (!playerAutoRetryUsedRef.current) {
      playerAutoRetryUsedRef.current = true
      setTimeout(() => {
        if (playerSessionRef.current !== session) return
        retryPlayer({ preserveAutoRetryBudget: true })
      }, 0)
      return
    }
    failPlayer(session, 'media')
  }

  const closePlayer = () => {
    playerSessionRef.current += 1
    clearPlayerTimers()
    revokePlayerBlob()
    const el = playerVideoRef.current
    if (el) {
      try {
        el.pause()
        el.removeAttribute('src')
        el.load()
      } catch {
        /* ignore cleanup races */
      }
    }
    setPlayerUrl(null)
    setPlayerTaskId(null)
    setPlayerTask(null)
    setPlayerPhase('idle')
    setPlayerSlowHint(false)
    setPlayerErrorKind(null)
    playerAutoRetryUsedRef.current = false
  }

  /** Download P0-B fix (2026-08-24) — root cause was this handler fetching
   * the ENTIRE video through axios as a `Blob` (`getVideoGenTaskOutput`,
   * `responseType: 'blob'`) before the browser's download UI ever
   * appeared: the "点击下载...响应明显异常缓慢" symptom was really "the
   * click waits for the whole multi-MB/multi-tens-of-MB transfer to land
   * in JS memory first", not a slow download itself. `?download=true`
   * (see `_deliver_video` in the backend) sets `Content-Disposition:
   * attachment` (with a safe `video-<id prefix>.mp4` filename baked in
   * server-side) on a real streamed response — no blob, no
   * `URL.createObjectURL`, no waiting for the transfer to finish before
   * the click "does" anything. Same `triggerBrowserDownload` helper the
   * image-asset download buttons already use for their own real
   * (non-blob) `download_url`s.
   *
   * Tenant-Branded Asset Download Delivery P0 (2026-08-25) update: this
   * endpoint used to 302-redirect straight to a short-lived presigned R2
   * URL — that leaked `*.r2.cloudflarestorage.com` as the browser's final
   * byte-transfer origin (visible in Network / macOS "Where From"
   * metadata, a real SaaS branding/privacy-boundary bug). It now proxies
   * real bytes back same-origin instead (see
   * `app.api.asset_delivery.stream_asset_response`) — this frontend
   * handler is unchanged because it never depended on the redirect; the
   * browser's own top-level navigation follows either response shape
   * identically. */
  const handleDownload = (task: VideoGenTask) => {
    if (!workspaceId) return
    triggerBrowserDownload(`${taskPlaybackUrl(workspaceId, task)}?download=true`)
  }

  const isFormDirty = useCallback(() => {
    return (
      prompt.trim().length > 0 ||
      !!refImage ||
      !!refVideo ||
      aspectRatio !== (cfg?.default_aspect_ratio ?? '9:16') ||
      qualityTier !== (cfg?.default_quality_tier ?? '1080P') ||
      duration !== (cfg?.default_duration_seconds ?? 10) ||
      enhancePrompt ||
      seedMode !== 'random'
    )
  }, [prompt, refImage, refVideo, aspectRatio, qualityTier, duration, enhancePrompt, seedMode, cfg])

  const applyRedo = useCallback(
    (task: VideoGenTask) => {
      setPrompt(task.prompt || '')
      setAspectRatio(task.generation.aspect_ratio || cfg?.default_aspect_ratio || '9:16')
      setQualityTier(task.generation.quality_tier)
      setDuration(task.generation.duration_seconds)
      setSeedMode(task.generation.seed_mode === 'fixed' ? 'fixed' : 'random')
      setSeed(task.generation.seed ?? null)
      setEnhancePrompt(!!task.enhance_prompt)

      const imgRef = task.input_assets.find((a) => a.role === 'reference_image')
      const vidRef = task.input_assets.find((a) => a.role === 'reference_video')
      setRefImage(
        imgRef
          ? {
              filename: t('generation.referenceImage'),
              previewUrl: videoGenAssetPosterUrl(workspaceId, imgRef.asset_id),
              status: 'ready',
              assetId: imgRef.asset_id,
            }
          : null,
      )
      setRefVideo(
        vidRef
          ? {
              filename: t('generation.referenceVideo'),
              previewUrl: videoGenAssetPosterUrl(workspaceId, vidRef.asset_id),
              status: 'ready',
              assetId: vidRef.asset_id,
            }
          : null,
      )
      if (imgRef || vidRef || task.enhance_prompt || task.generation.seed_mode === 'fixed') setAdvancedOpen(true)
      window.scrollTo({ top: 0, behavior: 'smooth' })
      message.success(t('generation.redo.restored'))
    },
    [cfg, workspaceId, t],
  )

  const handleRedo = (task: VideoGenTask) => {
    if (isFormDirty()) {
      Modal.confirm({
        centered: true,
        rootClassName: 'tc-video-gen-confirm-modal',
        title: t('generation.redo.confirmTitle'),
        content: t('generation.redo.confirmContent'),
        okText: t('generation.redo.continue'),
        cancelText: t('actions:cancel'),
        onOk: () => applyRedo(task),
      })
    } else {
      applyRedo(task)
    }
  }

  const canSubmit =
    prompt.trim().length > 0 &&
    !submitting &&
    refImage?.status !== 'uploading' &&
    refVideo?.status !== 'uploading' &&
    refImage?.status !== 'failed' &&
    refVideo?.status !== 'failed'

  const onSubmit = async () => {
    if (!workspaceId || !canSubmit) return
    setSubmitting(true)
    try {
      await createVideoGenTask(workspaceId, {
        prompt: prompt.trim(),
        aspect_ratio: aspectRatio,
        quality_tier: qualityTier,
        duration_seconds: duration,
        seed_mode: seedMode,
        seed: seedMode === 'fixed' ? seed : null,
        enhance_prompt: enhancePrompt,
        reference_image_asset_id: refImage?.assetId ?? null,
        reference_video_asset_id: refVideo?.assetId ?? null,
      })
      message.success(t('generation.toast.queued'))
      setPrompt('')
      clearRef('reference_image')
      clearRef('reference_video')
      setTab('all')
      loadTasks()
    } catch (e) {
      message.error((e as Error).message || t('generation.toast.createFailed'))
    } finally {
      setSubmitting(false)
    }
  }

  const removeIdsFromState = useCallback((ids: string[]) => {
    const gone = new Set(ids)
    setTasks((prev) => prev.filter((t) => !gone.has(t.id)))
    setSelectedIds((prev) => prev.filter((id) => !gone.has(id)))
    ids.forEach((id) => progressHighWaterRef.current.delete(id))
  }, [])

  const deleteOneTask = async (task: VideoGenTask) => {
    try {
      if (isContinuousComposite(task)) {
        await deleteVideoGenComposite(workspaceId, task.composite_id || task.id)
      } else {
        await deleteVideoGenTask(workspaceId, task.id)
        removeIdsFromState([task.id])
      }
      message.success(t('generation.toast.deleted'))
      await loadTasks()
    } catch (e) {
      message.error((e as Error).message || t('generation.toast.deleteFailed'))
      throw e
    }
  }

  const confirmBatchDelete = () => {
    const targets = tasks.filter((t) => selectedIds.includes(t.id))
    const deletable = targets.filter(canDeleteTask)
    const blocked = targets.length - deletable.length
    if (deletable.length === 0) {
      message.warning(blocked > 0 ? t('generation.batchDelete.allRunning') : t('generation.batchDelete.noneSelected'))
      return
    }
    const n = deletable.length
    Modal.confirm({
      centered: true,
      rootClassName: 'tc-video-gen-confirm-modal',
      title: t('generation.batchDelete.title', { count: n }),
      content:
        blocked > 0
          ? t('generation.batchDelete.contentWithBlocked', { blocked })
          : t('generation.batchDelete.content'),
      okText: t('generation.batchDelete.okText', { count: n }),
      okType: 'danger',
      cancelText: t('actions:cancel'),
      onOk: async () => {
        try {
          const { data } = await batchDeleteVideoGenTasks(
            workspaceId,
            deletable.map((t) => t.id),
          )
          removeIdsFromState(data.deleted)
          if (data.failed_count > 0) {
            message.warning(t('generation.batchDelete.partial', { deleted: data.deleted_count, failed: data.failed_count }))
          } else {
            message.success(t('generation.batchDelete.success', { count: data.deleted_count }))
          }
          await loadTasks()
        } catch (e) {
          message.error((e as Error).message || t('generation.batchDelete.failed'))
          throw e
        }
      },
    })
  }

  const goContinuation = (r: VideoGenTask) => {
    const parentId = r.root_video_id || r.source_video_id || r.id
    navigate(`/workspace/${workspaceId}/video-generation/continuation?parent_video_id=${parentId}`)
  }

  const resumeContinuationSession = (r: VideoGenTask) => {
    const sourceId = r.root_video_id || r.source_video_id
    if (!sourceId) return
    navigate(`/workspace/${workspaceId}/video-generation/continuation?parent_video_id=${sourceId}`)
  }

  const renderActionButtons = (r: VideoGenTask) => {
    const showDownload = canPlayTask(r)
    const showResumeContinuation = isContinuousComposite(r) && isMasterComposite(r)
    const showParamsRedo =
      (r.status === 'succeeded' || r.status === 'failed') && !isContinuousComposite(r) && !isLineageRow(r)
    const showContinuation =
      r.status === 'succeeded' && !!r.output_asset_id && !isContinuousComposite(r) && !isLineageRow(r)
    const showDelete = canDeleteTask(r)
    if (!showDownload && !showParamsRedo && !showContinuation && !showResumeContinuation && !showDelete) {
      return <span className="tc-video-gen-muted">—</span>
    }
    return (
      <span className="tc-video-gen-actions">
        {showDownload && (
          <Tooltip title={t('actions:download')} overlayClassName="tc-video-gen-tooltip">
            <Button size="small" type="text" icon={<DownloadOutlined />} onClick={() => handleDownload(r)} />
          </Tooltip>
        )}
        {showResumeContinuation && (
          <Tooltip title={t('generation.actions.continueGenerate')} overlayClassName="tc-video-gen-tooltip">
            <Button size="small" type="text" icon={<ForwardOutlined />} onClick={() => resumeContinuationSession(r)} />
          </Tooltip>
        )}
        {showContinuation && (
          <Tooltip title={t('generation.actions.continuation')} overlayClassName="tc-video-gen-tooltip">
            <Button size="small" type="text" icon={<ForwardOutlined />} onClick={() => goContinuation(r)} />
          </Tooltip>
        )}
        {showParamsRedo && (
          <>
            <Tooltip title={t('generation.actions.parameters')} overlayClassName="tc-video-gen-tooltip">
              <Button size="small" type="text" icon={<FileTextOutlined />} onClick={() => setParamsTask(r)} />
            </Tooltip>
            <Tooltip title={t('generation.actions.redo')} overlayClassName="tc-video-gen-tooltip">
              <Button size="small" type="text" icon={<RedoOutlined />} onClick={() => handleRedo(r)} />
            </Tooltip>
          </>
        )}
        {showDelete && (
          <VistoeraActionConfirm
            title={t('generation.actions.deleteTitle')}
            description={t('generation.actions.deleteDescription')}
            confirmLabel={t('actions:delete')}
            tooltip={t('actions:delete')}
            onConfirm={() => deleteOneTask(r)}
          >
            <Button size="small" type="text" danger icon={<DeleteOutlined />} />
          </VistoeraActionConfirm>
        )}
      </span>
    )
  }

  const tableData = useMemo(() => {
    const sorted = [...tasks].sort((a, b) => groupSortTimestamp(b) - groupSortTimestamp(a))
    return flattenGroupedTasks(sorted)
  }, [tasks])

  const taskTabs = useMemo(
    () => [
      { key: 'all', label: t('generation.tabs.all') },
      { key: 'queued', label: t('status:queued') },
      { key: 'running', label: t('status:generating') },
      { key: 'succeeded', label: t('status:succeeded') },
      { key: 'failed', label: t('status:failed') },
    ],
    [t],
  )

  const columns: ColumnsType<VideoGenTask> = useMemo(
    () => [
    {
      title: t('generation.table.video'),
      width: 96,
      render: (_, r) => {
        const thumb = canPlayTask(r) ? (
            <TaskPosterThumb src={taskPosterUrl(workspaceId, r)} onClick={() => openPlayer(r)} />
          ) : (
            <div className="tc-video-gen-table-thumb tc-video-gen-table-thumb-empty">
              {r.status === 'failed' ? <CloseCircleOutlined /> : <span>—</span>}
            </div>
          )
        // Video Continuation V1 (spec §10) — lightweight indent + connector
        // for a segment row; a plain video or a sequence root renders
        // exactly as before (no layout change).
        if (isMasterComposite(r)) {
          return thumb
        }
        if (isLineageRow(r) || r.segment_index != null) {
          return (
            <div
              className={`tc-video-gen-row-child${isContinuousComposite(r) ? ' tc-video-gen-row-composite' : ''}`}
            >
              <span className="tc-video-gen-row-connector" aria-hidden="true" />
              {thumb}
            </div>
          )
        }
        return thumb
      },
    },
    {
      title: t('generation.table.taskId'),
      dataIndex: 'id',
      width: 96,
      render: (id: string) => <span title={id}>{id.slice(0, 8)}</span>,
    },
    {
      title: t('generation.table.prompt'),
      dataIndex: 'prompt',
      ellipsis: true,
      width: 260,
      // P0-B spec §6: text-heavy column opts back to left.
      align: 'left' as const,
      render: (_p: string, r) => (
        <Tooltip title={taskPromptLabel(r, t)} placement="topLeft" overlayClassName="tc-video-gen-tooltip">
          <span className="tc-video-gen-prompt-cell">{taskPromptLabel(r, t)}</span>
        </Tooltip>
      ),
    },
    {
      title: t('generation.table.parameters'),
      width: 150,
      render: (_, r) => (
        <span className="tc-video-gen-muted-text">
          {t('generation.table.parametersValue', {
            seconds: r.generation.duration_seconds,
            quality: r.generation.quality_tier,
            ratio: r.generation.aspect_ratio,
          })}
        </span>
      ),
    },
    {
      title: t('generation.table.relation'),
      width: 130,
      render: (_, r) => {
        // Video Continuation V1 (spec §10) — "连续生成" badge + short
        // sequence id, shown on the root AND every segment of a sequence
        // (never on a plain, never-continued video).
        const sequenceId = r.sequence_id
        if (!sequenceId) return <span className="tc-video-gen-muted">—</span>
        if (isMasterComposite(r)) {
          return (
            <Tooltip title={t('generation.lineage.sequenceId', { id: sequenceId })} overlayClassName="tc-video-gen-tooltip">
              <span className="tc-badge tc-badge-info tc-video-gen-seq-badge">{t('generation.lineage.composite')}</span>
            </Tooltip>
          )
        }
        if (r.lineage_role === 'source') {
          return (
            <Tooltip title={t('generation.lineage.sequenceId', { id: sequenceId })} overlayClassName="tc-video-gen-tooltip">
              <span className="tc-badge tc-badge-info tc-video-gen-seq-badge">{t('generation.lineage.source')}</span>
            </Tooltip>
          )
        }
        return (
          <Tooltip title={t('generation.lineage.sequenceId', { id: sequenceId })} overlayClassName="tc-video-gen-tooltip">
            <span className="tc-badge tc-badge-info tc-video-gen-seq-badge">
              {r.segment_index != null
                ? t('generation.lineage.segment', { index: r.segment_index })
                : t('generation.actions.continuation')}
            </span>
          </Tooltip>
        )
      },
    },
    {
      title: t('generation.table.status'),
      width: 88,
      render: (_, r) => (
        <span className={`tc-badge ${STATUS_BADGE_CLASS[r.status] || 'tc-badge-neutral'}`}>{r.status_label}</span>
      ),
    },
    {
      title: t('generation.table.progress'),
      width: 110,
      render: (_, r) => {
        if (r.status === 'queued') {
          return (
            <span className="tc-video-gen-muted">
              {r.queue_position
                ? t('generation.table.queuedPosition', { position: r.queue_position })
                : t('status:queued')}
            </span>
          )
        }
        const pct = displayProgressPct(r)
        if (pct == null) return <span className="tc-video-gen-muted">—</span>
        const rounded = Math.round(pct)
        return (
          <span className="tc-video-gen-progress-cell" title={r.status === 'running' ? t('generation.table.progressEstimate') : undefined}>
            <span className="tc-video-gen-progress-bar" aria-hidden="true">
              <span className="tc-video-gen-progress-bar-fill" style={{ width: `${rounded}%` }} />
            </span>
            <span>{rounded}%</span>
          </span>
        )
      },
    },
    { title: t('generation.table.queuedAt'), width: 148, render: (_, r) => formatDateTime(r.queued_at, locale) },
    {
      title: t('generation.table.actions'),
      width: 148,
      fixed: 'right',
      render: (_, r) => renderActionButtons(r),
    },
  ],
  [t, locale, workspaceId, displayProgressPct, renderActionButtons],
  )

  const pagination: TablePaginationConfig = {
    current: tablePage,
    pageSize: PAGE_SIZE,
    total: tableData.length,
    size: 'small',
    showSizeChanger: false,
    onChange: (page) => setTablePage(page),
  }

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(tableData.length / PAGE_SIZE) || 1)
    if (tablePage > maxPage) setTablePage(maxPage)
  }, [tableData.length, tablePage])

  const aspectRatioChoices = cfg?.aspect_ratio_choices ?? FALLBACK_ASPECT_RATIO_CHOICES
  const aspectRatioOptions = useMemo(
    () =>
      aspectRatioChoices.map((r) => ({
        value: r,
        label: (
          <span className="tc-video-gen-ratio-option">
            <AspectRatioIcon ratio={r} size={18} />
            <span>{r}</span>
          </span>
        ),
      })),
    [aspectRatioChoices],
  )

  return (
    <div className="tc-items-page tc-video-gen-page">
      <div className="tc-page-header">
        <div>
          <h2 className="tc-page-title">{t('generation.title')}</h2>
          <p className="tc-page-desc">{t('generation.subtitle')}</p>
        </div>
        {/* VISTOERA_VIDEO_DIRECTOR_UI_PREVIEW_VISIBILITY_FORENSIC_P0 — this
         * page (prompt + 参考素材 + 视频参数 → 开始生成 + 任务列表) moved from
         * the bare `video-generation` URL to `video-generation/classic`;
         * the AI Director workspace is now the primary page at the bare
         * URL. This button is the way back — it stays fully intact and
         * reachable, nothing below was removed. */}
        <Button
          type="primary"
          icon={<ThunderboltOutlined />}
          onClick={() => navigate(`/workspace/${workspaceId}/video-generation`)}
        >
          {t('generation.aiDirectorButton')}
        </Button>
      </div>

      {cfgError && <div className="tc-video-gen-error">{t('generation.configLoadFailed', { error: cfgError })}</div>}

      <div className="tc-video-gen-create-row">
        <div className="tc-data-card tc-video-gen-create-card">
          <div className="tc-video-gen-card-title">{t('generation.createCardTitle')}</div>

          <label className="tc-video-gen-label">{t('generation.promptLabel')}</label>
          <Input.TextArea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value.slice(0, promptMax))}
            placeholder={t('generation.promptPlaceholder')}
            className="tc-video-gen-prompt"
            autoSize={{ minRows: 4, maxRows: 5 }}
          />
          <div className="tc-video-gen-char-count">
            {prompt.length}/{promptMax}
          </div>

          <label className="tc-video-gen-label">{t('generation.referenceMaterialsLabel')}</label>
          <div className="tc-video-gen-refs-row">
            <RefUploadCard
              kind="reference_image"
              label={t('generation.referenceImage')}
              hint={cfg ? cfg.image_allowed_mime.map((m) => m.split('/')[1]?.toUpperCase()).join(' / ') : 'JPG / PNG / WEBP'}
              state={refImage}
              onPick={() => pickFile('reference_image')}
              onClear={() => clearRef('reference_image')}
            />
            <RefUploadCard
              kind="reference_video"
              label={t('generation.referenceVideo')}
              hint={cfg ? cfg.video_allowed_mime.map((m) => m.split('/')[1]?.toUpperCase()).join(' / ') : 'MP4 / MOV / WEBM'}
              state={refVideo}
              onPick={() => pickFile('reference_video')}
              onClear={() => clearRef('reference_video')}
            />
          </div>
          <input
            ref={videoInputRef}
            type="file"
            accept={cfg?.video_allowed_mime.join(',') || 'video/*'}
            style={{ display: 'none' }}
            onChange={(e) => onFileSelected('reference_video', e.target.files?.[0])}
          />

          <div className="tc-video-gen-params-row">
            <div className="tc-video-gen-param">
              <label>{t('generation.durationLabel')}</label>
              <Select
                value={duration}
                onChange={setDuration}
                style={{ width: 100 }}
                popupClassName="tc-video-gen-select-popup"
                options={(cfg?.duration_preset_choices ?? [3, 5, 10, 15])
                  .filter((d) => d <= maxDurationForQuality)
                  .map((d) => ({ value: d, label: t('generation.durationSeconds', { seconds: d }) }))}
              />
            </div>
            <div className="tc-video-gen-param">
              <label>{t('generation.resolutionLabel')}</label>
              <Select
                value={qualityTier}
                onChange={setQualityTier}
                style={{ width: 130 }}
                popupClassName="tc-video-gen-select-popup"
                options={cfg?.quality_tier_choices ?? [{ value: '1080P', label: t('director.parameters.quality1080') }]}
              />
            </div>
            <div className="tc-video-gen-param">
              <label>{t('generation.aspectRatioLabel')}</label>
              <Select
                value={aspectRatio}
                onChange={setAspectRatio}
                style={{ width: 108 }}
                popupClassName="tc-video-gen-select-popup"
                options={aspectRatioOptions}
              />
            </div>
            <div className="tc-video-gen-param">
              <label>{t('generation.generationCountLabel')}</label>
              <Select
                value={1}
                disabled
                style={{ width: 70 }}
                popupClassName="tc-video-gen-select-popup"
                options={[{ value: 1, label: '1' }]}
              />
            </div>
            <Button
              type="primary"
              icon={<ThunderboltOutlined />}
              loading={submitting}
              disabled={!canSubmit}
              onClick={onSubmit}
              className="tc-video-gen-submit"
            >
              {t('generation.startGenerate')}
            </Button>
          </div>

          <div className="tc-video-gen-advanced-toggle" onClick={() => setAdvancedOpen((v) => !v)}>
            {t('generation.advancedToggle')} {advancedOpen ? '▲' : '▼'}
          </div>
          {advancedOpen && (
            <div className="tc-video-gen-advanced">
              <div className="tc-video-gen-param">
                <label>{t('generation.enhancePromptLabel')}</label>
                <Switch checked={enhancePrompt} onChange={setEnhancePrompt} size="small" />
              </div>
              <div className="tc-video-gen-param">
                <label>Seed</label>
                <Radio.Group value={seedMode} onChange={(e) => setSeedMode(e.target.value)} size="small">
                  <Radio.Button value="random">{t('generation.seedRandom')}</Radio.Button>
                  <Radio.Button value="fixed">{t('generation.seedFixed')}</Radio.Button>
                </Radio.Group>
              </div>
              {seedMode === 'fixed' && (
                <InputNumber
                  value={seed ?? undefined}
                  onChange={(v) => setSeed(v ?? null)}
                  min={cfg?.seed_range.min ?? 0}
                  max={cfg?.seed_range.max ?? 2147483647}
                  placeholder={t('generation.seedPlaceholder')}
                  style={{ width: 160 }}
                />
              )}
            </div>
          )}
        </div>

        <div className="tc-data-card tc-video-gen-info-card">
          <div className="tc-video-gen-card-title">
            <InfoCircleOutlined /> {t('generation.infoCardTitle')}
          </div>
          <ul className="tc-video-gen-info-list">
            <li>{t('generation.infoList.textOnly')}</li>
            <li>{t('generation.infoList.imageRef')}</li>
            <li>{t('generation.infoList.videoRef')}</li>
            <li>{t('generation.infoList.queue')}</li>
          </ul>
          <div className="tc-video-gen-info-note">
            {t('generation.infoNote')}
          </div>
        </div>
      </div>

      <div className="tc-data-card">
        <div className="tc-toolbar" style={{ justifyContent: 'space-between' }}>
          <Tabs
            activeKey={tab}
            onChange={setTab}
            items={taskTabs.map((tabItem) => ({ key: tabItem.key, label: tabItem.label }))}
            className="tc-video-gen-tabs"
          />
          <Button icon={<ReloadOutlined />} size="small" onClick={loadTasks} loading={tasksLoading}>
            {t('actions:refresh')}
          </Button>
        </div>
        {selectedIds.length > 0 && (
          <div className="tc-video-gen-batch-bar">
            <span>{t('generation.selection.count', { count: selectedIds.length })}</span>
            <Button size="small" danger icon={<DeleteOutlined />} onClick={confirmBatchDelete}>
              {t('actions:delete')}
            </Button>
            <Button size="small" type="text" onClick={() => setSelectedIds([])}>
              {t('generation.selection.clear')}
            </Button>
          </div>
        )}
        <div className="tc-data-table">
          <Table
            rowKey="id"
            size="small"
            columns={columns}
            dataSource={tableData}
            loading={tasksLoading}
            pagination={pagination}
            scroll={{ x: 1200 }}
            sticky={{ offsetHeader: 64 }}
            rowClassName={(record) =>
              isMasterComposite(record)
                ? 'tc-video-gen-row-composite-master'
                : isLineageRow(record) || record.segment_index != null
                  ? 'tc-video-gen-row-segment'
                  : ''
            }
            rowSelection={{
              selectedRowKeys: selectedIds,
              preserveSelectedRowKeys: true,
              onChange: (keys) => setSelectedIds(keys as string[]),
              getCheckboxProps: (record) => ({
                disabled: record.status === 'running',
                name: record.id,
              }),
            }}
          />
        </div>
      </div>

      <TenantVideoPlayerModal
        open={!!playerTaskId}
        onClose={closePlayer}
        title={
          playerTask && isMasterComposite(playerTask)
            ? t('generation.player.compositeTitle')
            : t('generation.player.defaultTitle')
        }
        videoUrl={playerUrl}
        posterUrl={workspaceId && playerTask ? taskPosterUrl(workspaceId, playerTask) : null}
        phase={playerPhase}
        slowHint={playerSlowHint}
        errorKind={playerErrorKind}
        reloadNonce={playerReloadNonce}
        videoRef={playerVideoRef}
        onReady={handlePlayerReady}
        onProgress={handlePlayerProgress}
        onError={handlePlayerError}
        onRetry={() => retryPlayer()}
      />

      <Modal
        open={!!refPreview}
        onCancel={() => setRefPreview(null)}
        footer={null}
        title={refPreview?.title || t('generation.refPreview.title')}
        width={refPreview?.kind === 'video' ? resolveTenantVideoPlayerModalWidth(window.innerWidth) : 640}
        destroyOnClose
        rootClassName="tc-video-gen-player-modal tc-tenant-video-player-modal"
      >
        {refPreview?.kind === 'image' ? (
          <img src={refPreview.url} alt="" style={{ width: '100%', maxHeight: '82vh', objectFit: 'contain' }} />
        ) : refPreview?.kind === 'video' ? (
          <div className="tc-tenant-video-player-stage">
            <video
              src={refPreview.url}
              controls
              autoPlay
              muted
              className="tc-tenant-video-player-video"
            />
          </div>
        ) : null}
      </Modal>

      <ParamsDrawer task={paramsTask} workspaceId={workspaceId} onClose={() => setParamsTask(null)} onPreviewRef={setRefPreview} />

      {siteId && (
        <ImageAssetPicker
          open={imagePickerOpen}
          workspaceId={workspaceId}
          siteId={siteId}
          purpose="video_input"
          title={t('generation.imagePickerTitle')}
          onCancel={() => setImagePickerOpen(false)}
          onConfirm={(selections) => {
            setImagePickerOpen(false)
            const sel = selections[0]
            if (!sel?.assetId) return
            setRefImage({
              filename: sel.filename || t('generation.referenceImage'),
              previewUrl: videoGenAssetPosterUrl(workspaceId, sel.assetId),
              status: 'ready',
              assetId: sel.assetId,
            })
          }}
        />
      )}
    </div>
  )
}

export function TaskPosterThumb({ src, onClick }: { src: string; onClick: () => void }) {
  const { t } = useTranslation(['video'])
  const [errored, setErrored] = useState(false)
  return (
    <div className="tc-video-gen-table-thumb" role="button" tabIndex={0} onClick={onClick} title={t('generation.actions.playTitle')}>
      {!errored ? (
        <>
          <img src={src} alt="" className="tc-video-gen-table-thumb-img" onError={() => setErrored(true)} />
          <PlayCircleFilled className="tc-video-gen-table-thumb-play" />
        </>
      ) : (
        <VideoCameraOutlined className="tc-video-gen-table-thumb-icon" />
      )}
    </div>
  )
}

function ParamRow({ label, value, block }: { label: string; value: ReactNode; block?: boolean }) {
  return (
    <div className={`tc-video-gen-params-row-item ${block ? 'tc-video-gen-params-row-block' : ''}`}>
      <div className="tc-video-gen-params-row-label">{label}</div>
      <div className="tc-video-gen-params-row-value">{value ?? '—'}</div>
    </div>
  )
}

function ParamsDrawer({
  task,
  workspaceId,
  onClose,
  onPreviewRef,
}: {
  task: VideoGenTask | null
  workspaceId: string
  onClose: () => void
  onPreviewRef: (state: RefPreviewState) => void
}) {
  const { t } = useTranslation(['video', 'actions'])
  const { locale } = useUiLocale()
  const imgRef = task?.input_assets.find((a) => a.role === 'reference_image')
  const vidRef = task?.input_assets.find((a) => a.role === 'reference_video')
  const g = task?.generation

  return (
    <Drawer
      title={t('generation.paramsDrawer.title')}
      open={!!task}
      onClose={onClose}
      width={440}
      destroyOnClose
      rootClassName="tc-video-gen-params-drawer"
    >
      {task && g && (
        <div className="tc-video-gen-params-view">
          <ParamRow label="Task ID" value={<span className="tc-video-gen-mono">{task.id}</span>} />
          <ParamRow label={t('generation.paramsDrawer.status')} value={task.status_label} />
          <ParamRow label={t('generation.paramsDrawer.prompt')} value={task.prompt || '—'} block />
          <ParamRow
            label={t('generation.paramsDrawer.enhancePrompt')}
            value={task.enhance_prompt ? t('generation.paramsDrawer.on') : t('generation.paramsDrawer.off')}
          />

          <div className="tc-video-gen-params-section-title">{t('generation.paramsDrawer.referenceMaterials')}</div>
          <div className="tc-video-gen-params-refs">
            <div className="tc-video-gen-params-ref-item">
              <div className="tc-video-gen-params-row-label">{t('generation.referenceImage')}</div>
              {imgRef ? (
                <button
                  type="button"
                  className="tc-video-gen-params-ref-frame"
                  onClick={() =>
                    onPreviewRef({
                      kind: 'image',
                      url: videoGenAssetContentUrl(workspaceId, imgRef.asset_id),
                      title: t('generation.referenceImage'),
                    })
                  }
                >
                  <img
                    src={videoGenAssetPosterUrl(workspaceId, imgRef.asset_id)}
                    alt=""
                    className="tc-video-gen-params-ref-media"
                  />
                </button>
              ) : (
                <span className="tc-video-gen-muted">—</span>
              )}
            </div>
            <div className="tc-video-gen-params-ref-item">
              <div className="tc-video-gen-params-row-label">{t('generation.referenceVideo')}</div>
              {vidRef ? (
                <button
                  type="button"
                  className="tc-video-gen-params-ref-frame tc-video-gen-params-ref-frame-video"
                  onClick={() =>
                    onPreviewRef({
                      kind: 'video',
                      url: videoGenAssetContentUrl(workspaceId, vidRef.asset_id),
                      title: t('generation.referenceVideo'),
                    })
                  }
                >
                  <img
                    src={videoGenAssetPosterUrl(workspaceId, vidRef.asset_id)}
                    alt=""
                    className="tc-video-gen-params-ref-media"
                  />
                  <PlayCircleFilled className="tc-video-gen-params-ref-play" />
                </button>
              ) : (
                <span className="tc-video-gen-muted">—</span>
              )}
            </div>
          </div>

          <div className="tc-video-gen-params-section-title">{t('generation.paramsDrawer.generationParams')}</div>
          <ParamRow label={t('generation.paramsDrawer.duration')} value={t('generation.paramsDrawer.durationValue', { seconds: g.duration_seconds })} />
          <ParamRow label={t('generation.paramsDrawer.resolution')} value={g.quality_tier} />
          <ParamRow
            label={t('generation.paramsDrawer.aspectRatio')}
            value={
              <span className="tc-video-gen-ratio-option">
                <AspectRatioIcon ratio={g.aspect_ratio} size={16} />
                <span>{g.aspect_ratio}</span>
              </span>
            }
          />
          <ParamRow label={t('generation.paramsDrawer.generationCount')} value="1" />
          <ParamRow
            label="Seed"
            value={
              g.seed_mode === 'fixed'
                ? t('generation.paramsDrawer.seedValueFixed', { seed: g.seed })
                : t('generation.paramsDrawer.seedValueRandom', { seed: g.seed ?? '—' })
            }
          />

          <div className="tc-video-gen-params-section-title">{t('generation.paramsDrawer.advancedParams')}</div>
          <ParamRow label={t('generation.paramsDrawer.outputQuality')} value={g.output_quality ?? '—'} />
          <ParamRow
            label={t('generation.paramsDrawer.frameRate')}
            value={g.frame_rate != null ? t('generation.paramsDrawer.frameRateValue', { rate: g.frame_rate }) : '—'}
          />
          <ParamRow label={t('generation.paramsDrawer.imageStrength')} value={g.image_strength != null ? g.image_strength : '—'} />
          <ParamRow label={t('generation.paramsDrawer.keyframeCount')} value={g.gen_kf != null ? g.gen_kf : '—'} />

          <div className="tc-video-gen-params-section-title">{t('generation.paramsDrawer.timeline')}</div>
          <ParamRow label={t('generation.paramsDrawer.createdAt')} value={formatDateTime(task.queued_at, locale)} />
          <ParamRow label={t('generation.paramsDrawer.startedAt')} value={formatDateTime(task.started_at, locale)} />
          <ParamRow label={t('generation.paramsDrawer.finishedAt')} value={formatDateTime(task.finished_at, locale)} />
          {task.status === 'queued' && (
            <ParamRow
              label={t('generation.paramsDrawer.queuePosition')}
              value={
                task.queue_position
                  ? t('generation.paramsDrawer.queuePositionValue', { position: task.queue_position })
                  : t('status:queued')
              }
            />
          )}
          {task.error_message && (
            <ParamRow
              label={t('generation.paramsDrawer.errorInfo')}
              value={task.error_code ? `[${task.error_code}] ${task.error_message}` : task.error_message}
              block
            />
          )}
        </div>
      )}
    </Drawer>
  )
}

export function RefUploadCard({
  kind,
  label,
  hint,
  state,
  onPick,
  onClear,
}: {
  kind: RefKind
  label: string
  hint: string
  state: RefAssetState | null
  onPick: () => void
  onClear: () => void
}) {
  const { t } = useTranslation(['video', 'actions'])
  if (state) {
    return (
      <div className="tc-video-gen-ref-card tc-video-gen-ref-card-filled">
        {state.status === 'uploading' && (
          <div className="tc-video-gen-ref-overlay">
            <LoadingOutlined /> {t('generation.refUpload.uploading')}
          </div>
        )}
        {state.status === 'failed' && (
          <div className="tc-video-gen-ref-overlay tc-video-gen-ref-overlay-error">
            {t('generation.refUpload.uploadFailed', { error: state.error })}
          </div>
        )}
        {state.previewUrl ? (
          <div className="tc-video-gen-ref-preview">
            <img src={state.previewUrl} alt={state.filename} className="tc-video-gen-ref-thumb" />
            {kind === 'reference_video' && <PlayCircleFilled className="tc-video-gen-ref-video-badge" />}
          </div>
        ) : (
          <VideoCameraAddOutlined className="tc-video-gen-ref-icon" />
        )}
        <div className="tc-video-gen-ref-filename" title={state.filename}>
          {state.filename}
        </div>
        <Button
          size="small"
          type="text"
          icon={<CloseCircleOutlined />}
          className="tc-video-gen-ref-remove"
          onClick={onClear}
        />
      </div>
    )
  }
  return (
    <div className="tc-video-gen-ref-card" onClick={onPick} role="button" tabIndex={0}>
      <PictureOutlined className="tc-video-gen-ref-icon" />
      <div>{label}</div>
      <div className="tc-video-gen-ref-hint">{hint}</div>
    </div>
  )
}
